import { ProjectValidationUnit } from '../../../../models/data-structures/project-validation';
import { FinancialInvestment, FinancialInvestmentPropName } from '../../../../models/project/financial/financial-investment/financial-investment.model';
import { ValidationInterface } from '../../validation.interface';
import { UnitValueValidation } from '../unit-value.validation';
import { LoanValidation } from './loan.validation';
import { PartnershipValidation } from './partnership.validation';

export class FinancialInvestmentValidation implements ValidationInterface {
  /** The current financial investment obj that is being validated */
  financialInvestment: FinancialInvestment;

  /**
   * Sets the financial investment obj that will be used for validation
   * @param invFinances will be used to set the current investment finances obj
   */
  constructor(invFinances: FinancialInvestment) {
    if (!invFinances) throw Error('invalid investment finances provided for validation');
    this.financialInvestment = new FinancialInvestment(invFinances);
  }

  /**
   * Get a validation unit containing the validation data for the current investment finances obj
   */
  getValidation(): ProjectValidationUnit {
    const validationUnit = new ProjectValidationUnit();

    validationUnit.addModuleResults(
      new UnitValueValidation(this.financialInvestment.discountRate).getValidation(),
      FinancialInvestmentPropName.discountRate
    );
    validationUnit.addModuleResults(
      new UnitValueValidation(this.financialInvestment.incomeTax).getValidation(),
      FinancialInvestmentPropName.incomeTax
    );
    validationUnit.addModuleResults(
      new UnitValueValidation(this.financialInvestment.residualValue).getValidation(),
      FinancialInvestmentPropName.residualValue
    );
    validationUnit.addModuleResults(
      new UnitValueValidation(this.financialInvestment.distributions).getValidation(),
      FinancialInvestmentPropName.distributions
    );

    this.addLoanValidation(validationUnit);
    this.addPartnershipValidation(validationUnit);
    return validationUnit;
  }

  /**
   * returns a boolean that determines if the financial investment finances obj is valid
   */
  isValid(): boolean {
    return this.getValidation().isValid;
  }

  /**
   * Adds validation for loans
   * @param validationUnit is the validation unit for which the income validation will be added to
   */
  private addLoanValidation(validationUnit: ProjectValidationUnit) {
    if (this.financialInvestment && this.financialInvestment.loans && this.financialInvestment.loans.length > 0) {
      for (let i = 0; i < this.financialInvestment.loans.length; i++) {
        validationUnit.addArrayToModuleResults(
          new LoanValidation(this.financialInvestment.loans[i]).getValidation(),
          FinancialInvestmentPropName.loans,
          i,
          this.financialInvestment.loans[i].name
        );
      }
    }
  }

  /**
   * Adds validation for partnerships
   * @param validationUnit is the validation unit for which the income validation will be added to
   */
  private addPartnershipValidation(validationUnit: ProjectValidationUnit) {
    if (this.financialInvestment && this.financialInvestment.partnerships && this.financialInvestment.partnerships.length > 0) {
      for (let i = 0; i < this.financialInvestment.partnerships.length; i++) {
        validationUnit.addArrayToModuleResults(
          new PartnershipValidation(this.financialInvestment.partnerships[i]).getValidation(),
          FinancialInvestmentPropName.partnerships,
          i,
          this.financialInvestment.partnerships[i].name
        );
      }
    }
  }
}

enum FinancialInvestmentError {
  CONTRIBUTION_PERCENTAGE_NOT_100 = 'VALIDATION.CONTRIBUTION_PERCENTAGE_NOT_100',
  SHARE_PERCENTAGE_NOT_100 = 'VALIDATION.SHARE_PERCENTAGE_NOT_100'
}
